import { TRoles, TRoutes, TWidgetDomains } from './const'

/*
 * Der Weg von einer Kachel der „Allgemeinen Information" in die Liste dahinter.
 *
 * Die Kachel „Unternehmen" nennt drei Zahlen: den Bestand, die Zugänge der
 * letzten 24 Stunden und die Ausstehenden. Bis 26.08.2026 war keine davon
 * anklickbar — wer die zwölf ausstehenden Unternehmen sehen wollte, ging ins
 * Menü, öffnete die Liste, klappte die Filterleiste auf und baute den Filter
 * von Hand nach. Drei Handgriffe für eine Zahl, die schon auf dem Bildschirm
 * steht.
 *
 * Dieselbe Überlegung wie bei shared/ticketListLink: Der Vertrag steht hier und
 * nicht in der Kachel, damit beide Seiten dieselben Wörter verwenden. Die
 * Listen lesen ihre Filter über UI/FilterPanel/useFilters aus der Adresszeile
 * (`?f_<schlüssel>=<wert>`) — schriebe die Kachel einen anderen Schlüssel,
 * landete man stillschweigend in einer ungefilterten Liste, und die Zahl daneben
 * sähe falsch aus.
 *
 * **Jede Zahl führt genau auf die Zeilen, die sie zählt.** Deshalb hat jede
 * Zelle ihr eigenes Ziel, statt dass die ganze Kachel auf die Liste zeigt: Wer
 * „Ausstehend: 6" anklickt, will sechs Zeilen sehen und nicht 891.
 */

// Schlüssel, unter denen useFilters die Werte in der Adresszeile erwartet.
const PARAM_PREFIX = 'f_'
const SEPARATOR = ','

/*
 * Zeitraum der Zelle „Neu".
 *
 * Die Widget-Zahl zählt, was seit 24 Stunden dazugekommen ist (siehe
 * api/private/admin/widgets: `createdAt >= heute - 1 Tag`). Der Filter der
 * Listen rechnet in Tagen, damit dieselbe Auswahl auch von Hand sinnvoll ist
 * („letzte 7 Tage"). Eins heißt hier also: genau der Zeitraum der Kachel.
 */
export const NEW_WITHIN_DAYS = 1

/**
 * Adresse einer Liste mit vorgewählten Filtern.
 *
 * @param {string} route Ziel-Ansicht aus TRoutes.
 * @param {Object.<string, (string|number|boolean|Array)>} filter Feldschlüssel → Wert(e).
 */
export const listLink = (route, filter = {}) => {
	const params = new URLSearchParams()

	Object.entries(filter).forEach(([key, value]) => {
		const werte = (Array.isArray(value) ? value : [value]).filter(
			wert => wert !== undefined && wert !== null && wert !== ''
		)
		if (werte.length) params.set(PARAM_PREFIX + key, werte.join(SEPARATOR))
	})

	const query = params.toString()
	return query ? `${route}?${query}` : route
}

/*
 * Was die Zellen einer Kachel je Bereich bedeuten — und wo die Zeilen dazu
 * stehen.
 *
 * Die Zellnamen sind die Schlüssel, unter denen der Server die Zahlen liefert
 * (api/private/admin/widgets): `total`, `new`, `pending`, `registered`,
 * `active`, bei den Rechnungen zusätzlich `credit` und `cancelled`. Die
 * Drei-Zahlen-Kachel nutzt drei davon, die Vier-Zahlen-Kachel alle — beide
 * fragen dieselbe Stelle.
 *
 * Die `hints` stehen hier und nicht in der Kachel, weil dasselbe Wort je
 * Bereich etwas anderes heißt: „Ausstehend" ist bei den Unternehmen eine
 * Prüfung, bei den Rechnungen ein Geldeingang, bei den Schüler:innen die
 * Durchsicht der Elternbestätigung. Ein Wort für drei Sachverhalte ist der
 * Grund, warum niemand weiß, was die Zahl von ihm will.
 *
 * Es sind i18n-Schlüssel, keine fertigen Sätze — die Kachel übersetzt sie
 * (`widgetHints`).
 *
 * `filter: null` heißt: Diese Zelle hat kein Ziel. Besser als ein Verweis auf
 * eine Liste, in der etwas anderes steht.
 */
const BEREICHE = {
	[TWidgetDomains.COMPANIES]: {
		route: TRoutes.ADMIN_COMPANIES,
		basis: {},
		zellen: {
			total: { filter: {}, hint: 'All Junior Companies on record.' },
			new: { filter: { createdWithin: NEW_WITHIN_DAYS }, hint: 'Created in the last 24 hours.' },
			pending: {
				filter: { companyStatus: 'IS_NEED_REVIEW' },
				hint: 'Submitted for review — waiting to be activated by the administration.'
			},
			registered: { filter: { companyStatus: 'NOT_ACTIVATED' }, hint: 'Registered and not yet activated.' },
			/*
			 * Der Filter fordert zusätzlich `isNeedReview = false`, die Kachel
			 * zählt nur `isActive = true`. Verschieden wäre das nur für eine
			 * Junior Company, die zugleich aktiv ist **und** erneut zur Prüfung
			 * steht — den Zustand gibt es im Ablauf nicht, und wenn doch, ist
			 * die Zeile in „Ausstehend" besser aufgehoben.
			 */
			active: { filter: { companyStatus: 'ACTIVATED' }, hint: 'Activated Junior Companies.' }
		}
	},
	[TWidgetDomains.STUDENTS]: {
		route: TRoutes.USERS,
		basis: { role: TRoles.STUDENT },
		zellen: {
			total: { filter: {}, hint: 'All students on record.' },
			new: { filter: { createdWithin: NEW_WITHIN_DAYS }, hint: 'Created in the last 24 hours.' },
			/*
			 * Nicht „hat sich nicht bestätigt", sondern: Die Elternbestätigung
			 * liegt hochgeladen vor und ist noch nicht durchgesehen. Genau das
			 * zählt der Server (kein Prüfvermerk, aber ein Dokument der Art
			 * PARENTAL_CONFIRMATION vorhanden) — und genau dabei kann die
			 * Verwaltung helfen: Die Zeile der Liste trägt die Handlung
			 * „Elternbestätigung bestätigen".
			 */
			pending: {
				filter: { reviewedParentalConfirmation: 'SUBMITTED' },
				hint: 'Parental confirmation uploaded and not yet checked.'
			},
			active: {
				filter: { reviewedParentalConfirmation: 'CHECKED' },
				hint: 'Parental confirmation checked.'
			},
			/*
			 * „Registriert" ist beim Server eine Restgröße (alle minus geprüft
			 * minus eingereicht) und keine Eigenschaft — dafür gibt es keinen
			 * Filter, der dieselbe Menge trifft.
			 */
			registered: { filter: null, hint: 'Neither uploaded nor checked yet.' }
		}
	},
	[TWidgetDomains.TEACHERS]: {
		route: TRoutes.USERS,
		basis: { role: TRoles.TEACHER },
		zellen: {
			total: { filter: {}, hint: 'All teachers on record.' },
			new: { filter: { createdWithin: NEW_WITHIN_DAYS }, hint: 'Created in the last 24 hours.' },
			pending: {
				filter: { verifiedTeacher: false },
				hint: 'Not yet released — waiting to be confirmed by the administration.'
			},
			// Beim Server dieselbe Zählung wie „Ausstehend" (verifiedTeacher =
			// false) — also auch dasselbe Ziel. Zwei Zahlen, eine Menge.
			registered: {
				filter: { verifiedTeacher: false },
				hint: 'Not yet released — waiting to be confirmed by the administration.'
			},
			active: { filter: { verifiedTeacher: true }, hint: 'Released teachers.' }
		}
	},
	[TWidgetDomains.INVOICES]: {
		route: TRoutes.INVOICES,
		basis: {},
		zellen: {
			/*
			 * Die Kachel zählt ohne die stornierten (die stehen eigens
			 * daneben, #91) — also führt sie auch in die Liste ohne sie.
			 * Blank verwiesen stünden in der Liste mehr Zeilen, als die Zahl
			 * darüber nennt.
			 */
			total: {
				filter: { invoiceStatus: ['Paid', 'Unpaid', 'Overdue', 'Credit'] },
				hint: 'All invoices except the cancelled ones.'
			},
			new: { filter: { createdWithin: NEW_WITHIN_DAYS }, hint: 'Issued in the last 24 hours.' },
			pending: { filter: { invoiceStatus: 'Unpaid' }, hint: 'Issued and not yet paid.' },
			registered: { filter: { invoiceStatus: 'Paid' }, hint: 'Paid invoices.' },
			active: { filter: { invoiceStatus: 'Overdue' }, hint: 'Overdue invoices.' },
			credit: { filter: { invoiceStatus: 'Credit' }, hint: 'Invoices in credit.' },
			cancelled: { filter: { invoiceStatus: 'Cancelled' }, hint: 'Cancelled invoices.' }
		}
	},
	/*
	 * Berichte: **keine** Ziele. Der Menüpunkt „Berichte" führt auf eine
	 * Ansicht mit zwölf erfundenen Zeilen (views/admin/ReportsTable) — dorthin
	 * zu verweisen hieße, eine echte Zahl gegen erfundene Daten einzutauschen.
	 * Und „Überfällig" kommt vom Server bis heute als feste 0 zurück
	 * (api/private/admin/widgets, `getReports`).
	 *
	 * Der Eintrag steht trotzdem hier, damit die Kachel ihre erklärenden Sätze
	 * bekommt — und damit diese Datei sagt, dass der Bereich bedacht wurde,
	 * statt dass er stillschweigend durchfällt.
	 */
	[TWidgetDomains.REPORTS]: {
		route: null,
		basis: {},
		zellen: {
			total: { filter: null, hint: 'All submitted reports.' },
			new: { filter: null, hint: 'Submitted in the last 24 hours.' },
			pending: { filter: null, hint: 'This figure is currently not collected.' }
		}
	},
	/*
	 * Die Finanzkachel zeigt Beträge, keine Datensätze — „Aufwendungen:
	 * 12.480 €" führt auf keine Liste, die genau diese Summe enthält.
	 */
	[TWidgetDomains.REPORT_DATA]: {
		route: null,
		basis: {},
		zellen: {}
	}
}

/**
 * Die Ziele der Zellen einer Kachel.
 *
 * @param {string} domain Bereich der Kachel (TWidgetDomains).
 * @param {string} [state] Bundesland, auf das die Kachel eingestellt ist.
 * @returns {Object.<string, ?string>} Zellname → Adresse (oder `null`).
 */
export const widgetLinks = (domain, state) => {
	const bereich = BEREICHE[domain]
	if (!bereich?.route) return {}

	// Das Bundesland der Kachel wandert mit in die Liste: Die Kachel des
	// Bundesadmins lässt sich auf ein Land einstellen, und ihre Zahl gilt dann
	// nur dort. Landesadmins bekommen ihr Land ohnehin vom Server gesetzt —
	// der zusätzliche Filter schadet dort nicht, er sagt dasselbe noch einmal.
	const land = state ? { state } : {}

	return Object.entries(bereich.zellen).reduce((ziele, [name, zelle]) => {
		ziele[name] = zelle.filter ? listLink(bereich.route, { ...bereich.basis, ...land, ...zelle.filter }) : null
		return ziele
	}, {})
}

/**
 * Die erklärenden Sätze zu den Zahlen einer Kachel.
 *
 * @param {string} domain Bereich der Kachel (TWidgetDomains).
 * @param {function} t Übersetzerin aus useTranslation.
 * @returns {Object.<string, string>} Zellname → Satz.
 */
export const widgetHints = (domain, t) => {
	const bereich = BEREICHE[domain]
	if (!bereich) return {}

	return Object.entries(bereich.zellen).reduce((saetze, [name, zelle]) => {
		saetze[name] = zelle.hint ? t(zelle.hint) : ''
		return saetze
	}, {})
}
